import * as React from 'react';
import { View } from 'react-native';
import getSymbolFromCurrency from 'currency-symbol-map';
import { runOnJS, useAnimatedProps, useDerivedValue, } from 'react-native-reanimated';
import { rem, signPlusSymbolWidth, Spacer, symbolWidth, ToolTip, } from 'design-system';
import BlurAmountView from 'design-system/BlurAmountView';
import Text, { styles as textStyles } from 'design-system/Text';
import { AnimatedTextInput } from 'design-system/AnimatedComponents';
import { CurrencyAmountText } from 'design-system/CurrencyAmountText';
import useStyles from 'hooks/useStyles';
import { selectIsScrambled } from 'hooks';
import { useAppSelector } from 'store/hooks';
import createStyleSheets from 'utils/createStyleSheets';
import { formatAmount, formatAmountWorklet } from 'utils/formatting';
import { selectDefaultZeroAmount, selectFeed } from 'reducers/selectors';
import { selectTotalDebtsBalance, selectTotalAssetsBalance, } from '../selectors';
import { useBalances } from '../hooks/useBalances';
import { isIOS, isWeb, isAndroid } from '../../../constants';
export const tooltipStrings = {
    Everyday: {
        title: 'Everyday balance',
        desc: 'This is the total of all your available balances minus the total of all your credit card balances. It can help you understand what your purchasing power is. You can see how this value changes over time in the graph.',
    },
    'Net Worth - Main': {
        title: 'Net worth',
        desc: 'This is your total assets minus your total debts. Tap on Assets or Debts to see the breakdown.',
    },
    'Net Worth - Asset': {
        title: 'Assets',
        desc: 'This is the total balance of your Current, Savings, Investment, Pension, Real Estate, Vehicle, and Other asset accounts. It doesn’t include debts, so it may be higher than your actual net worth.',
    },
    'Net Worth - Debt': {
        title: 'Debt',
        desc: 'This is the total of all your credit card balances and loans',
    },
};
const chartTitle = {
    Everyday: 'Everyday',
    Savings: 'Savings',
    Investments: 'Investments',
    'Net Worth - Main': 'Net worth',
    'Net Worth - Asset': 'Assets',
    'Net Worth - Debt': 'Debt',
};
const AccountChartInfoView = ({ title, lastItem, selectedInfoObj, isAssetVsDebt, }) => {
    const styles = useStyles(styleSet);
    const didMount = React.useRef(false);
    const [internalLastItem, setInternalLastItem] = React.useState(lastItem);
    React.useEffect(() => {
        if (didMount.current) {
            if (lastItem !== undefined) {
                setInternalLastItem(lastItem);
            }
        }
        else {
            didMount.current = true;
        }
    }, [lastItem]);
    const feed = useAppSelector(selectFeed);
    const balances = useBalances(feed.cards.overview);
    const zero = useAppSelector(selectDefaultZeroAmount);
    const isScrambled = useAppSelector(selectIsScrambled);
    const totalDebtBalance = useAppSelector(selectTotalDebtsBalance);
    const totalAssetsBalance = useAppSelector(selectTotalAssetsBalance);
    const fallbackItem = React.useMemo(() => {
        switch (title) {
            case 'Everyday':
            case 'Savings':
            case 'Investments': {
                return balances.find((item) => item.title === title) || zero;
            }
            case 'Net Worth - Main': {
                return balances.find((item) => item.title === 'Net worth') || zero;
            }
            case 'Net Worth - Debt': {
                return { ...totalDebtBalance, amount: totalDebtBalance.amount * -1 };
            }
            case 'Net Worth - Asset': {
                return totalAssetsBalance;
            }
            default: {
                return zero;
            }
        }
    }, [balances, title, totalAssetsBalance, totalDebtBalance, zero]);
    const currencyStr = React.useMemo(() => getSymbolFromCurrency(fallbackItem.currency), [fallbackItem.currency]);
    const fallbackInfo = React.useMemo(() => {
        const amount = formatAmount(Math.abs(fallbackItem.amount), undefined, 2);
        const sign = fallbackItem.amount < 0 ? '-' : '';
        const [decimal, fraction] = amount.split('.');
        return {
            decimal,
            fraction: `.${fraction}`,
            currencyAndSign: sign + currencyStr,
            currencyAndSignWidth: sign ? signPlusSymbolWidth : symbolWidth,
        };
    }, [currencyStr, fallbackItem.amount]);
    const infoSharedValue = useDerivedValue(() => {
        if (selectedInfoObj.value) {
            const valueToUse = isAssetVsDebt &&
                'networthAmount' in selectedInfoObj.value &&
                typeof selectedInfoObj.value.networthAmount === 'number'
                ? selectedInfoObj.value.networthAmount
                : selectedInfoObj.value.actualY;
            const amount = formatAmountWorklet(Math.abs(valueToUse), '');
            const sign = valueToUse < 0 ? '-' : '';
            const [decimal, fraction] = amount.split('.');
            return {
                decimal,
                fraction: `.${fraction}`,
                currencyAndSign: sign + selectedInfoObj.value.currencySymbol,
                currencyAndSignWidth: sign ? signPlusSymbolWidth : symbolWidth,
            };
        }
        return fallbackInfo;
    }, [fallbackInfo, selectedInfoObj, isAssetVsDebt]);
    const decimalProps = useAnimatedProps(() => ({
        text: infoSharedValue.value.decimal,
    }), [infoSharedValue]);
    const currencyProps = useAnimatedProps(() => ({
        text: infoSharedValue.value.currencyAndSign,
        ...(isAndroid
            ? {
                width: infoSharedValue.value.currencyAndSignWidth,
            }
            : {}),
    }), [infoSharedValue]);
    const fractionProps = useAnimatedProps(() => ({
        text: infoSharedValue.value.fraction,
    }), [infoSharedValue]);
    const timeProps = useAnimatedProps(() => ({
        text: selectedInfoObj.value
            ? String(selectedInfoObj.value.label)
            : internalLastItem?.label || '',
    }), [internalLastItem?.label, selectedInfoObj]);
    const [webState, setWebState] = React.useState(undefined);
    useDerivedValue(() => {
        if (isWeb) {
            runOnJS(setWebState)(selectedInfoObj.value);
        }
    }, [selectedInfoObj]);
    const ToolTipContent = React.useMemo(() => {
        const tooltipInfo = tooltipStrings[title];
        if (tooltipInfo) {
            return (<>
          <Spacer w={4}/>
          <ToolTip small title={tooltipInfo.title} subtitle={tooltipInfo.desc}/>
        </>);
        }
        return null;
    }, [title]);
    const AmountContent = React.useMemo(() => {
        if (isWeb) {
            const value = webState
                ? {
                    amount: isAssetVsDebt &&
                        'networthAmount' in webState &&
                        typeof webState.networthAmount === 'number'
                        ? webState?.networthAmount
                        : webState.actualY,
                    currency: webState.currency,
                }
                : fallbackItem;
            return (<>
          <CurrencyAmountText value={value} size={'L'}/>
          <Text TextThin-14 Secondary>
            {webState?.label || internalLastItem?.label || ' '}
          </Text>
        </>);
        }
        return (<>
        <View style={styles.row}>
          <BlurAmountView enabled={isScrambled}>
            <View style={styles.amountView}>
              <AnimatedTextInput editable={false} numberOfLines={1} allowFontScaling={false} animatedProps={currencyProps} defaultValue={fallbackInfo.currencyAndSign} style={[
                styles.currencyStyles,
                isAndroid && { width: fallbackInfo.currencyAndSignWidth },
            ]}/>
              <Spacer w={2}/>
              <AnimatedTextInput editable={false} numberOfLines={1} allowFontScaling={false} animatedProps={decimalProps} style={styles.absAmountStyles} defaultValue={fallbackInfo.decimal}/>
              <AnimatedTextInput editable={false} numberOfLines={1} allowFontScaling={false} animatedProps={fractionProps} style={styles.fractionStyles} defaultValue={fallbackInfo.fraction}/>
            </View>
          </BlurAmountView>
        </View>
        <AnimatedTextInput editable={false} numberOfLines={1} allowFontScaling={false} style={styles.timeStyles} animatedProps={timeProps} defaultValue={internalLastItem?.label || ''}/>
      </>);
    }, [
        currencyProps,
        decimalProps,
        fallbackInfo.currencyAndSign,
        fallbackInfo.currencyAndSignWidth,
        fallbackInfo.decimal,
        fallbackInfo.fraction,
        fallbackItem,
        fractionProps,
        internalLastItem?.label,
        isAssetVsDebt,
        isScrambled,
        styles.absAmountStyles,
        styles.amountView,
        styles.currencyStyles,
        styles.fractionStyles,
        styles.row,
        styles.timeStyles,
        timeProps,
        webState,
    ]);
    return (<View style={styles.container}>
      <View style={styles.titleContainer}>
        <Text Text-14 Secondary>
          {chartTitle[title]}
        </Text>
        {ToolTipContent}
      </View>
      {AmountContent}
    </View>);
};
export default React.memo(AccountChartInfoView);
const styleSet = createStyleSheets((colors) => ({
    row: {
        flexDirection: 'row',
    },
    container: {
        padding: rem(16),
    },
    currencyStyles: {
        margin: 0,
        padding: 0,
        fontSize: rem(30),
        lineHeight: rem(isIOS ? 41 : 42),
        color: colors.text.primary,
        fontFamily: 'Nunito-ExtraBold',
    },
    absAmountStyles: {
        margin: 0,
        padding: 0,
        fontSize: rem(36),
        lineHeight: rem(48),
        color: colors.text.primary,
        fontFamily: 'Nunito-ExtraBold',
    },
    fractionStyles: {
        margin: 0,
        padding: 0,
        fontSize: rem(24),
        lineHeight: rem(38),
        color: colors.text.primary,
        paddingTop: rem(isIOS ? 0 : 4),
        fontFamily: 'Nunito-ExtraBold',
    },
    amountView: {
        paddingTop: rem(6),
        flexDirection: 'row',
        paddingBottom: rem(2),
    },
    timeStyles: {
        margin: 0,
        padding: 0,
        color: colors.text.secondary,
        fontSize: textStyles.TextThin_14.fontSize,
        fontFamily: textStyles.TextThin_14.fontFamily,
    },
    titleContainer: {
        alignItems: 'center',
        flexDirection: 'row',
    },
}));
